// @flow
import React, { Component } from "react";
import { connect } from "react-redux";
import { Switch, Route } from "react-router-dom";
import { createStructuredSelector } from "reselect";
import { consts as uploaderConsts } from "uploader";
import t from "common/i18n";
import getLogger from "common/logger";
import TabPanel from "tabs/hocs/TabPanel";
import ServiceLoginIntro from "root/components/ServiceLoginIntro";
import RouteWithSubRoutes from "root/components/RouteWithSubRoutes";
import TabProgress from "root/components/Progress/TabProgress";
import TabHeader from "root/components/TabHeader";
import TabFooter from "root/components/TabFooter";
import { selectIsCropEnabled, selectNewTlsDomain } from "root/selectors";
import { clearTabSelectionOnNewBatch } from "../common/utils";
import ContextItems from "./components/ContextItems";
import getDropbox from "./service";
import dbConsts from "./data/consts";
import getNavItems from "./routes/navItems";
import bindActions, { types as ats } from "./actions";
import {
	selectTabData, selectContextItems, selectAuthData, selectExtDropboxKey, selectContextSelectedItems,
} from "./selectors";
import SVG_ICONS from "~/assets/icons/icons";

import "./Dropbox.scss";

const logger = getLogger();

type Props = {
	addToUploadQueue: Function,
	authData: {
		token: string,
	},
	tabData: {
		userName: string,
		isLoggedIn: boolean,
		searchTerm: string,
		currentContext: number,
		cloudName: string,
		isMultiple: boolean,
		batchStartCounter: number,
	},
	contextData: {
		selected: Object[],
	},
	actions: { [string]: Function },
	dropBoxAppKey: ?string,
	selectedItems: Object[],
	routes: Object[],
	isCropEnabled: boolean,
	newTlsDomain?: boolean
};

export const listenForAuth = (props: Props): Promise<any> => {
	let result;

	if (!props.tabData.isLoggedIn) {
		const setAuthInfoAction = props.actions[ats.SET_AUTH_INFO];
		const { cloudName } = props.tabData;

		props.actions[ats.INIT_AUTH](cloudName);

		result = getDropbox().setAuthListener(cloudName)
			.then((result) => setAuthInfoAction({ token: result.token }));
	}

	return result || Promise.resolve(); // returning for test purposes
};

export class DropBoxTab extends Component<Props> {
	state = {
		isFetching: false,
	}

	componentDidMount() {
		listenForAuth(this.props);
	}

	componentDidUpdate(prevProps: Props) {
		listenForAuth(this.props);

		clearTabSelectionOnNewBatch(this.props, prevProps,
			this.props.actions[ats.CLEAR_ALL_SELECTION]); // clear the selection only when batch successfully started
	}

	onClearSelectedClick = () => {
		this.props.actions[ats.CLEAR_IMAGE_SELECTION](this.props.tabData.currentContext);
	};

	onNoAuthActionClick = () => {
		getDropbox().openAuthWindow({
			externalKey: this.props.dropBoxAppKey,
			newTlsDomain: this.props.newTlsDomain
		});
	};

	onUserLogout = () => {
		this.props.actions[ats.DO_LOGOUT]();
	};

	onUploadButtonClick = () => {
		this.setState({ isFetching: true });
		getDropbox().getUrlsForUpload({
			token: this.props.authData.token,
			files: this.props.selectedItems,
			shouldCreateBinary: this.props.isCropEnabled,
		})
			.then((files) => {
				this.setState({ isFetching: false });
				this.props.addToUploadQueue({ files });
			})
			.catch((err) => {
				this.setState({ isFetching: false });
				logger.error("[dropboxTab]: encountered error when trying to upload files", err);
			});
	};

	onSearchTermChange = (term: string) => {
		this.props.actions[ats.SET_SEARCH_TERM]({ term });
	};

	onSearchClick = () => {
		this.props.actions[ats.GET_CONTEXT_FILES](dbConsts.CONTEXTS.SEARCH,
			{ term: this.props.tabData.searchTerm });
	};

	renderSubRoutes(): Object[] {
		return this.props.routes.map((r: {| path: string |}) => <RouteWithSubRoutes
			key={`db_route_${r.path}`} {...r}
			isMultiple={this.props.tabData.isMultiple}/>);
	}

	renderDefaultRoute() {
		return <Route render={() => <ContextItems
			context={dbConsts.CONTEXTS.FOLDER}
			isMultiple={this.props.tabData.isMultiple}/>}/>;
	}

	renderTabContent() {
		let content = null;

		if (this.props.tabData.isLoggedIn && this.props.tabData.userName) {
			content = this.renderAuthedContent();
		} else {
			content = <ServiceLoginIntro key="db_login_intro"
			                             title={t("dropbox.no_auth_title")}
			                             actionTitle={t("dropbox.no_auth_action")}
			                             btnClass="btn-dropbox"
			                             dataTest="dropbox-connect-btn"
			                             btnIcon={SVG_ICONS.DROPBOX}
			                             actionHandler={this.onNoAuthActionClick}/>;
		}

		return content;
	}

	renderAuthedContent() {
		return <div className="tabs-block tab-authed flex flex-column">
			<TabHeader showSearch
			           source={uploaderConsts.SOURCES.DROPBOX}
			           navItems={getNavItems()}
			           userName={this.props.tabData.userName}
			           searchTerm={this.props.tabData.searchTerm}
			           onLogout={this.onUserLogout}
			           searchTermHandler={this.onSearchTermChange}
			           searchClickHandler={this.onSearchClick}/>

			<Switch>
				{this.renderSubRoutes()}
				{this.renderDefaultRoute()}
			</Switch>
		</div>;
	}

	renderFooter() {
		const ctxData = this.props.contextData;
		const selectionCount = ctxData && ctxData.selected && ctxData.selected.length;

		return <TabFooter showSelection
		                  selectionCount={selectionCount}
		                  clearSelectionHandler={this.onClearSelectedClick}
		                  uploadButtonHandler={this.onUploadButtonClick}
						  actionButtonActive={!!selectionCount && !this.state.isFetching}/>;
	}

	render() {
		const isInProgress = this.state.isFetching;
		return <div
			className={`dropbox-block h-100 h-auto-ns ${this.props.tabData.isLoggedIn && this.props.tabData.userName ? "user-active" : ""}`}>
			<div className="holder large-thin-menu-holder relative">
				{this.renderTabContent()}
				{isInProgress && <TabProgress/>}
			</div>

			{this.renderFooter()}
		</div>;
	}
}

export default TabPanel({
	id: uploaderConsts.SOURCES.DROPBOX,
})(connect(
	createStructuredSelector({
		tabData: selectTabData,
		contextData: selectContextItems,
		authData: selectAuthData,
		dropBoxAppKey: selectExtDropboxKey,
		selectedItems: selectContextSelectedItems,
		isCropEnabled: selectIsCropEnabled,
		newTlsDomain: selectNewTlsDomain
	}),
	(dispatch: Function) => ({
		actions: bindActions(dispatch),
	}),
)(DropBoxTab));
